import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Header = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`);
      setNotifications(res.data.slice(0, 10));
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`);
      setUnreadCount(res.data.count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`);
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  return (
    <header className="header" data-testid="header">
      <div className="header-left">
        <h1 className="header-title">FleetFlow</h1>
      </div>

      <div className="header-right">
        {/* Language Selector */}
        <DropdownMenu>
          <DropdownMenuTrigger className="language-selector" data-testid="language-selector">
            <span>{i18n.language.toUpperCase()}</span>
            <ChevronDown size={16} />
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => changeLanguage('bg')}>Български</DropdownMenuItem>
            <DropdownMenuItem onClick={() => changeLanguage('en')}>English</DropdownMenuItem>
            <DropdownMenuItem onClick={() => changeLanguage('ro')}>Română</DropdownMenuItem>
            <DropdownMenuItem onClick={() => changeLanguage('de')}>Deutsch</DropdownMenuItem>
            <DropdownMenuItem onClick={() => changeLanguage('es')}>Español</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger className="notification-bell" data-testid="notification-bell">
            <Bell size={20} />
            {unreadCount > 0 && (
              <span className="notification-badge" data-testid="notification-badge">{unreadCount}</span>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent className="notification-dropdown">
            {notifications.length === 0 ? (
              <div className="notification-empty">No notifications</div>
            ) : (
              notifications.map(notif => (
                <DropdownMenuItem
                  key={notif.id}
                  onClick={() => markAsRead(notif.id)}
                  className={`notification-item ${notif.is_read ? 'read' : 'unread'}`}
                  data-testid={`notification-${notif.id}`}
                >
                  <div className="notification-title">{notif.title}</div>
                  <div className="notification-message">{notif.message}</div>
                </DropdownMenuItem>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default Header;
